/**
 * eyeFollowSupport — which characters the Eye Follow node can actually drive
 * (ENG-1060).
 *
 * The node exists in the FACE graph, which every character has, but what it
 * writes only reaches some renderers: ARKit `EyeLook*` morphs on a GLB rig,
 * and the rig-level eye-aim controls on a splat character whose pipeline
 * drives a live rig (`src/ogs/rig/eyeFollowRig.js`). On anything else the node
 * would sit in the graph looking active and do nothing at all. That silence is
 * the bug this module exists to prevent: rather than ship a control that
 * quietly does nothing, the node reports the reason inline and disables its
 * fields, per the rule `voiceInputState.js`'s header sets out — unavailable
 * controls are shown and disabled WITH the reason, never hidden and never left
 * looking functional.
 *
 * SPLAT SUPPORT IS A RUNTIME FACT, not a class fact. Whether the rig route
 * works depends on the loaded bundle (does its control space carry the eye-aim
 * controls? does its pipeline drive a live rig at all?), which only OGSModel
 * knows once the content packs resolve. So OGSModel PUBLISHES the capability
 * here (`setSplatEyeFollowCapability`) and the class check consults it. While
 * nothing is published yet (boot, or a non-splat character) the splat branch
 * reports SUPPORTED — the same optimism rule as `known: false` below: a wrong
 * "unsupported" badge on every splat character during load is worse than a
 * late-arriving correct one.
 *
 * Takes the CLASSIFICATION rather than the character row, for two reasons: the
 * predicates (`isSplatCharacter` / `isStreamCharacter`) stay single-source in
 * `characterState.js` with no copy here to drift from them, and this module
 * stays importable under `node --test` — `characterState.js` is not, because of
 * its extensionless relative imports, and mocking half the app to reach one
 * decision is what `graphGroup.js` exists as the precedent against.
 */

/** Fallback splat reason — OGSModel normally publishes a more specific one. */
export const EYE_FOLLOW_SPLAT_REASON =
  'Not supported on this Gaussian-splat character — its pipeline exposes no '
  + 'live rig for the eye aim to ride.'

export const EYE_FOLLOW_STREAM_REASON =
  'Not supported on streamed characters yet — the server owns the face there, '
  + 'and Eye Follow is not wired into that pipeline.'

// The loaded splat bundle's capability, published by OGSModel once its content
// packs resolve: { route: 'rig' } (the rig-level eye-aim path is live),
// { route: 'none', reason } (this bundle/pipeline genuinely can't), or null
// (nothing loaded / torn down — pending).
let _splatCapability = null

/** OGSModel-only writer. Pass null on character teardown. */
export function setSplatEyeFollowCapability(capability) {
  _splatCapability = capability ?? null
}

/** Read-only view, for diagnostics and tests. */
export function getSplatEyeFollowCapability() {
  return _splatCapability
}

/**
 * @param {{isSplat?: boolean, isStream?: boolean, known?: boolean}} classification
 *   `known: false` (the default when no character has resolved yet) reports
 *   supported — the node must not accuse a character of being unsupported just
 *   because the character list hasn't loaded. A wrong "unsupported" badge on
 *   every rig character during boot is worse than a late-arriving correct one.
 * @returns {{supported: boolean, reason: string|null, renderer: 'rig'|'splat'|'stream'|'unknown'}}
 */
export function eyeFollowSupport({ isSplat = false, isStream = false, known = false } = {}) {
  if (!known) return { supported: true, reason: null, renderer: 'unknown' }
  // Stream is checked FIRST: `?stream=1` can stream a splat character, and the
  // reason the user needs is the one describing what is actually rendering.
  if (isStream) return { supported: false, reason: EYE_FOLLOW_STREAM_REASON, renderer: 'stream' }
  if (isSplat) {
    const cap = _splatCapability
    if (!cap || cap.route === 'rig') return { supported: true, reason: null, renderer: 'splat' }
    return { supported: false, reason: cap.reason || EYE_FOLLOW_SPLAT_REASON, renderer: 'splat' }
  }
  return { supported: true, reason: null, renderer: 'rig' }
}
